import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useContext, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import throttle from 'lodash/throttle';
import stripUnit from 'polished/lib/helpers/stripUnit';

import { ScrollContext, ScrollSection, useIsMobile } from '@tmh-website/shared';
import { theme } from '@tmh-website/styles/theme';

/**
 * Adds a timestamp to the ScrollContext that logs when a scroll section updated/rerendered.
 * This is used to reinitate the useActiveScrollSectionObserver in case that the reference to an
 * html element is lost after rerendering.
 * @param sectionRef
 */
export const useUpdateLastScrollSectionChange = (sectionRef: RefObject<HTMLElement>) => {
  const { setLastScrollSectionChange } = useContext(ScrollContext);
  useEffect(() => {
    setLastScrollSectionChange(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRef.current]);
};

/**
 * Observe the provided sections and set the currently visible section in the provided state
 * @param scrollSections
 * @param scrollSectionInView
 * @param setScrollSectionInView
 */
export const useActiveScrollSectionObserver = (
  scrollSections: ScrollSection[],
  setScrollSectionInView: Dispatch<SetStateAction<ScrollSection | null>>
) => {
  const isMobile = useIsMobile();
  const { headerIsVisible } = useContext(ScrollContext);

  useEffect(() => {
    if (!scrollSections || scrollSections.length === 0) {
      return () => {
        return;
      };
    }

    let startPos: number, stopPos: number;
    const firstEntryEl = scrollSections[0].element;
    const lastEntryEL = scrollSections[scrollSections.length - 1].element;

    function onResize() {
      if (!firstEntryEl || !lastEntryEL) {
        // Invalid element provided, therefore we stop this
        Sentry.captureMessage('Invalid element provided to useActiveScrollSectionObserver');
        return;
      }
      const startStopPadding = 50;
      startPos = firstEntryEl.offsetTop - startStopPadding;
      stopPos = lastEntryEL.offsetTop + lastEntryEL.clientHeight + startStopPadding;
    }
    onResize();

    function onScroll() {
      let padding = 0;
      if (headerIsVisible) {
        padding += isMobile === true ? +stripUnit(theme.headerHeightMobile) : +stripUnit(theme.headerHeight);
      }
      padding += isMobile === true ? +stripUnit(theme.subnavHeightMobile) : +stripUnit(theme.subnavHeight);
      const scrollPadding = padding; // This is the largest possible offset by combining the navbar and pagenavigation height + one pixel leeway
      const currentScrollOffset = window.scrollY + scrollPadding;

      // set scrollSectionInView to null if the scroll position is outside of the first and last element
      if (startPos && stopPos && (currentScrollOffset < startPos || currentScrollOffset > stopPos)) {
        setScrollSectionInView(null);
      }

      // Set the last section to the active section
      let newSectionInView;
      for (const scrollSection of scrollSections) {
        if (!scrollSection.element) {
          continue;
        }
        if (currentScrollOffset >= scrollSection.element.offsetTop - 1) {
          newSectionInView = scrollSection;
        } else {
          break;
        }
      }
      if (newSectionInView) {
        setScrollSectionInView(newSectionInView);
      }
    }

    const throttledOnScroll = throttle(onScroll, 100);
    const throttledOnResize = throttle(onResize, 100);
    window.addEventListener('scroll', throttledOnScroll);
    window.addEventListener('resize', throttledOnResize);

    return () => {
      window.removeEventListener('scroll', throttledOnScroll);
      window.addEventListener('resize', throttledOnResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollSections]);
};

/**
 * Disable scrolling on the body
 */

export function disableScrolling() {
  document.body.style.overflow = 'hidden';
  document.body.style.touchAction = 'none';
}

export function enableScrolling() {
  document.body.style.removeProperty('overflow');
  document.body.style.removeProperty('touch-action');
}

/**
 * Track how far the user scrolled into a section
 * @param sectionRef
 * @param setScrollProgress
 * @param throttleScroll Disable/enable throttling of the scroll event
 */
export const useSectionScrollProgress = (
  sectionRef: RefObject<HTMLDivElement>,
  setScrollProgress: Dispatch<SetStateAction<number>>,
  throttleScroll?: boolean
) => {
  useEffect(() => {
    let startPos: number, stopPos: number;

    function onResize() {
      if (!sectionRef.current) {
        return;
      }
      startPos = sectionRef.current.offsetTop;
      stopPos = sectionRef.current.offsetTop + sectionRef.current.clientHeight - window.innerHeight;
    }
    onResize();

    function onScroll() {
      const currentScrollOffset = window.scrollY;

      if (currentScrollOffset < startPos) {
        setScrollProgress(0);
      } else if (currentScrollOffset > stopPos) {
        setScrollProgress(1);
      } else {
        setScrollProgress((currentScrollOffset - startPos) / (stopPos - startPos));
      }
    }

    const throttledOnScroll = throttleScroll ?? true ? throttle(onScroll, 100) : onScroll;
    const throttledOnResize = throttle(onResize, 100);
    window.addEventListener('scroll', throttledOnScroll);
    window.addEventListener('resize', throttledOnResize);

    return () => {
      window.removeEventListener('scroll', throttledOnScroll);
      window.addEventListener('resize', throttledOnResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRef]);
};
